/**
 * กระบี่แท่งหมึกสน (ink-slate), hand-made in the sample style (was the C3 generator skeleton). The art's own look: a
 * scholar's sword wielded like a brush in a flurry of ink — fat calligraphy strokes (pressed start, thinning body,
 * dry-brush "flying white" tail), ink blots and splashes with a wet sheen, pine needles in pairs and pine sprigs (the
 * hall's pine), rice-paper white, and a square cinnabar seal (jz-seal's own mark, carved with our pine sign). Ink-grey
 * blade arcs with a white lead. Every move is staged anticipation -> strike -> impact -> dissipation:
 *   jz-stroke  one great brush stroke across the foe, running ink drips;
 *   jz-poem    (lock) three stabs, each writing one mark of a five-mark column of verse beside the foe, the column rains;
 *   jz-ink     a gob of ink flicked onto the marked area: a ground splash that pools (slow) and dries;
 *   jz-seal    a needle-fine jab to a vital point, the cinnabar seal stamped onto it, the meridians shut (silence);
 *   jz-ward    (buff) a folding fan painted with a pine opens behind the body, ribs glinting, folds into an ink ring;
 *   jz-focus   (buff) an ink circle brushed round the feet in one stroke, one drop, pine tufts held still;
 *   jz-break   (break) an original glyph written in the air stroke by stroke, driven into the foe and shattered;
 *   jz-scroll  (ท่าพลัง) a scroll unrolled over the head, written, wrapped round the caster and burst outward.
 * Original designs: the glyph, the verse marks and the seal sign are made-up shapes (no real text, no reference art).
 */
import { add, diamond, hash, isoRing, lerp, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import {
  bladeArc,
  C,
  clamp01,
  fade,
  hitStar,
  level,
  ringOn,
  since,
  strikeAt,
  tilesPx,
  type ArtFx,
} from '../families/swordKit';

// ── The art's pieces ──

/** Side sign of the strike (screen x): shapes mirror with the facing, not with the iso slope. */
const sideOf = (s: Scene): number => (s.d[0] >= 0 ? 1 : -1);

/** Scratch point + tangent of the quadratic curve (no per-sample allocation). */
const BP = [0, 0];
const BT = [0, 0];
function bzAt(k: readonly number[], u: number): void {
  const m = 1 - u;
  BP[0] = m * m * k[0]! + 2 * m * u * k[2]! + u * u * k[4]!;
  BP[1] = m * m * k[1]! + 2 * m * u * k[3]! + u * u * k[5]!;
  BT[0] = 2 * m * (k[2]! - k[0]!) + 2 * u * (k[4]! - k[2]!);
  BT[1] = 2 * m * (k[3]! - k[1]!) + 2 * u * (k[5]! - k[3]!);
}

/**
 * A calligraphy stroke along the quadratic curve k = [x0, y0, cx, cy, x1, y1] (screen cells): a pressed, slightly
 * bulging start, the body `w` rows, tapering to one row; toward the tail the bristles part (`dry` 0..1: rows dropped
 * as streaks of "flying white"). The upper row a sheen tone, a wet gloss at the press. Drawn from 0 to `upto` (the brush
 * head moving), at dither `lv`. `tone`: the body's ink tone (1 fresh, 2-3 drying).
 */
function brush(g: G, k: readonly number[], w: number, upto: number, dry: number, seed: number, lv: number, tone = 1): void {
  if (upto <= 0 || lv <= 0) return;
  const L = Math.hypot(k[2]! - k[0]!, k[3]! - k[1]!) + Math.hypot(k[4]! - k[2]!, k[5]! - k[3]!);
  const n = Math.max(6, Math.ceil(L * 2.2));
  level(g, lv);
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    if (u > upto) break;
    bzAt(k, u);
    const tl = Math.hypot(BT[0]!, BT[1]!) || 1;
    let nx = -BT[1]! / tl;
    let ny = BT[0]! / tl;
    if (ny > 0 || (ny === 0 && nx < 0)) {
      nx = -nx;
      ny = -ny;
    }
    const prof = u < 0.1 ? 1.3 - u * 1.5 : u < 0.4 ? 1.15 - (u - 0.1) * 0.5 : Math.max(0.1, ((1 - u) / 0.6) * 1.0);
    const rows = Math.max(1, Math.round(w * prof));
    const thr = dry * clamp01((u - 0.3) / 0.5);
    // The very end of a dry stroke breaks into separate bristle flecks.
    if (dry > 0 && u > 0.88 && (i >> 1) % 2 === 1) continue;
    for (let r = 0; r < rows; r++) {
      const off = r - (rows - 1) / 2;
      const top = r === rows - 1 && rows > 1;
      // A dry tail splits into separate fine bristle lines (1-cell lines two cells apart, so they stay apart).
      if (dry >= 0.5 && u > 0.6 && rows >= 3 && r % 3 !== 0) continue;
      // Parted bristles: streak rows in the light ink tone ("flying white" on the dark body).
      const streak = rows > 2 && r > 0 && !top && hash(r * 13 + 1, seed) < thr * 0.9;
      const col = top
        ? u < 0.1 && i % 3 === 0
          ? C('white', 6)
          : C('ink', 5)
        : streak
          ? C('white', 3)
          : C('ink', tone);
      px(g, BP[0]! + nx * off, BP[1]! + ny * off, col);
    }
  }
  g.globalAlpha = 1;
}

/** An ink blot at (x, y): ragged round edge (radius r, squashed by `sy` on the ground), sheen rim upper right, a gloss. */
function blot(g: G, x: number, y: number, r: number, seed: number, lv: number, sy = 1, sat = 0): void {
  if (lv <= 0 || r < 0.5) return;
  x = Math.round(x);
  y = Math.round(y);
  level(g, lv);
  const R0 = Math.ceil(r * 1.25);
  for (let dy = -R0; dy <= R0; dy++)
    for (let dx = -R0; dx <= R0; dx++) {
      const yy = dy / sy;
      const d = Math.hypot(dx, yy);
      const bin = Math.floor(((Math.atan2(yy, dx) + Math.PI) / (Math.PI * 2)) * 9) % 9;
      const rr = r * (0.78 + 0.42 * hash(bin, seed));
      if (d > rr) continue;
      px(g, x + dx, y + dy, dx > 0 && yy < 0 && d > rr - 1.5 ? C('ink', 4) : C('ink', 1));
    }
  if (r >= 2.5) px(g, x + Math.round(r * 0.35), y - Math.round(r * 0.35 * sy), C('white', 6));
  for (let j = 0; j < sat; j++) {
    const a = hash(j, seed + 5) * Math.PI * 2;
    const dd = r + 2 + hash(j, seed + 6) * r * 0.9;
    const sx = x + Math.cos(a) * dd;
    const sY = y + Math.sin(a) * dd * sy;
    if (j % 2 === 0) {
      g.fillStyle = C('ink', 1);
      g.fillRect(Math.round(sx), Math.round(sY), 2, 2);
    } else px(g, sx, sY, C('ink', 2));
  }
  g.globalAlpha = 1;
}

/** One flying ink drop (2x2 with a sheen cell, or a single speck). */
function inkDrop(g: G, x: number, y: number, small: boolean): void {
  if (small) {
    px(g, x, y, C('ink', 2));
    return;
  }
  g.fillStyle = C('ink', 1);
  g.fillRect(Math.round(x), Math.round(y), 2, 2);
  px(g, x + 1, y, C('ink', 4));
}

/** Ink drops flung from `c` round angle `dir` within `spread`, falling under gravity, dithered out over `life`. */
function spatter(g: G, c: V, e: number, n: number, dir: number, spread: number, speed: number, seed: number, life = 9, grav = 0.3): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.5, life * 0.8));
  for (let i = 0; i < n; i++) {
    const a = dir + (hash(i, seed) - 0.5) * spread;
    const v = speed * (0.6 + hash(i, seed + 1) * 0.8);
    inkDrop(g, c[0] + Math.cos(a) * v * e, c[1] + Math.sin(a) * v * e + grav * e * e, i % 3 === 2);
  }
  g.globalAlpha = 1;
}

/** A pine-needle pair: two 3-cell needles in a narrow V from (x, y), turned by `turn` (0..7, eighths of a turn). */
function needles(g: G, x: number, y: number, turn: number): void {
  const a = (((turn % 8) + 8) % 8) * (Math.PI / 4);
  for (const k of [-0.35, 0.35]) {
    const dx = Math.cos(a + k);
    const dy = Math.sin(a + k);
    px(g, x + dx, y + dy, C('leaf', 3));
    px(g, x + dx * 2, y + dy * 2, C('leaf', 4));
    px(g, x + dx * 3, y + dy * 3, C('leaf', 5));
  }
  px(g, x, y, C('brown', 4));
}

/** Pine-needle pairs flung out from `c`, drifting down and turning, dithered out. */
function needleBurst(g: G, c: V, e: number, n: number, seed: number, life = 12, speed = 2.2): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.55, life * 0.8));
  for (let i = 0; i < n; i++) {
    const a = hash(i, seed) * Math.PI * 2;
    const v = speed * (0.5 + hash(i, seed + 1));
    const drift = Math.sin(e * 0.7 + i) * 1.5;
    needles(g, c[0] + Math.cos(a) * v * e + drift, c[1] + Math.sin(a) * v * e * 0.6 + e * 0.6, i + (e >> 1));
  }
  g.globalAlpha = 1;
}

/** A pine sprig: a brown twig from (x, y) along `ang` for `len` cells, needle fans at three nodes, lit tips. */
function pine(g: G, x: number, y: number, ang: number, len: number, lv: number, upto = 1): void {
  if (lv <= 0 || upto <= 0) return;
  level(g, lv);
  const ux = Math.cos(ang);
  const uy = Math.sin(ang);
  const L = len * upto;
  line(g, [x, y], [x + ux * L, y + uy * L], C('brown', 3));
  for (let j = 1; j <= 3; j++) {
    const f = j / 3;
    if (f > upto + 0.01) break;
    const nx = x + ux * len * f;
    const ny = y + uy * len * f;
    const nl = j === 3 ? 6 : 5;
    for (let q = -3; q <= 3; q++) {
      const a = ang + q * 0.42 + (j === 3 ? 0 : 0.2);
      for (let s = 1; s <= nl; s++)
        px(g, nx + Math.cos(a) * s, ny + Math.sin(a) * s, s === nl ? C('leaf', 5) : s > nl / 2 ? C('leaf', 4) : C('leaf', 3));
    }
  }
  g.globalAlpha = 1;
}

/** The seal's carved sign (our own pine-and-hill mark, 6 x 6): 1 = carved (paper shows through). */
const SIGN = [
  0, 0, 1, 0, 0, 0,
  0, 1, 1, 1, 0, 0,
  1, 1, 1, 1, 1, 0,
  0, 0, 1, 0, 0, 1,
  1, 0, 1, 0, 1, 1,
  1, 1, 1, 1, 1, 1,
] as const;
/**
 * The cinnabar seal: a square `sc` x (6 + 2 margin + 2 border) cells centred at (x, y), squashed to `wy` of its height
 * (the press), red body, darker border, the sign carved in paper white, a worn edge.
 */
function chop(g: G, x: number, y: number, sc: number, lv: number, wy = 1, seed = 3): void {
  if (lv <= 0) return;
  const S = 10 * sc;
  const H = Math.max(2, Math.round(S * wy));
  const x0 = Math.round(x - S / 2);
  const y0 = Math.round(y - H / 2);
  level(g, lv);
  for (let j = 0; j < H; j++)
    for (let i = 0; i < S; i++) {
      const v = Math.floor((j / H) * S);
      const edge = i < sc || i >= S - sc || v < sc || v >= S - sc;
      if (edge && hash(i * 31 + v, seed) < 0.12) continue;
      const ci = Math.floor(i / sc) - 2;
      const cj = Math.floor(v / sc) - 2;
      const carved = ci >= 0 && ci < 6 && cj >= 0 && cj < 6 && SIGN[cj * 6 + ci] === 1;
      px(g, x0 + i, y0 + j, carved ? C('white', 6) : edge ? C('maroon', 3) : i + v < S * 0.5 ? C('maroon', 5) : C('maroon', 4));
    }
  g.globalAlpha = 1;
}

/** An ink bead held at the weapon tip (loading the brush), swelling from r 1 to 2. */
function bead(g: G, s: Scene, t0: number, t1: number): void {
  if (s.t < t0 || s.t >= t1) return;
  const { tip } = s.limbs(s.t);
  blot(g, tip[0], tip[1] + 1, s.t < (t0 + t1) / 2 ? 1.2 : 2.2, 41, 3);
  level(g, 3);
  px(g, tip[0] + 1, tip[1] - 1, C('white', 8));
  g.globalAlpha = 1;
}

// ── 1. jz-stroke: one great brush stroke across the foe, the ink running ──

const STROKE_HIT = 0.6;
const strokeCurve = (s: Scene, out: number[]): number[] => {
  const c = strikeAt(s);
  const sx = sideOf(s);
  out[0] = c[0] - sx * 34;
  out[1] = c[1] - s.chest * 1.2;
  out[2] = c[0] + sx * 6;
  out[3] = c[1] - s.chest * 0.8;
  out[4] = c[0] + sx * 10;
  out[5] = c[1] + s.chest * 0.85;
  return out;
};
const K1: number[] = [0, 0, 0, 0, 0, 0];
const stroke: ArtFx = {
  id: 'brush-stroke',
  skill: 'jz-stroke',
  th: 'jz-stroke',
  on: true,
  note: 'หยดหมึกบวมที่ปลายกระบี่ (จุ่มพู่กัน) -> ตวัดเป็นลายพู่กันใหญ่หนึ่งเส้นเฉียงผ่านเป้า หัวกดหนา ปลายแห้งแตกเป็นเส้นขาว -> ดาวกระแทก หมึกสาด ใบสนคู่กระเด็น -> หมึกไหลย้อยเป็นทางลงพื้น เส้นจางหาย',
  weapon: 'sword',
  acts: [{ m: 'wxCleave', t0: 0.2, t1: 1.0 }],
  hits: [STROKE_HIT],
  big: STROKE_HIT,
  ghosts: [0.52, 0.56],
  stepIn: 4,
  pre: 0.4,
  hit: STROKE_HIT,
  end: 1.75,
  afterimage: 'port',
  under(g, s) {
    // Where the running ink lands: small blots on the ground under the stroke.
    const k = strokeCurve(s, K1);
    for (let i = 0; i < 3; i++) {
      const e = since(s.t, 1.05 + i * 0.08);
      if (e < 0 || e >= 9) continue;
      bzAt(k, 0.15 + i * 0.25);
      blot(g, BP[0]!, s.f[1] + 2 - (i % 2) * 3, 2.5 + (e < 2 ? e : 2), 50 + i, fade(e, 5, 7), 0.5);
    }
  },
  over(g, s) {
    bead(g, s, 0.22, 0.46);
    bladeArc(g, s, 0.44, 0.7, 'ink', 3);
    const k = strokeCurve(s, K1);
    const e = since(s.t, STROKE_HIT - 1 / 12);
    if (e >= 0 && e < 13) {
      const upto = e === 0 ? 0.55 : 1;
      brush(g, k, 8, upto, 0.75, 11, e < 5 ? 3 : e < 9 ? 2 : 1, e < 6 ? 1 : 2);
      // The ink runs: drips growing down from the stroke's wet body, a bead at each head.
      if (e >= 3) {
        const run = Math.min(14, (e - 3) * 2);
        for (let i = 0; i < 3; i++) {
          bzAt(k, 0.15 + i * 0.25);
          const x = BP[0]!;
          const y = BP[1]! + 3;
          level(g, e < 9 ? 3 : 2);
          line(g, [x, y], [x, y + run - i * 2], C('ink', 1));
          line(g, [x + 1, y], [x + 1, y + run - i * 2 - 2], C('ink', 5));
          inkDrop(g, x, y + run - i * 2, false);
        }
        g.globalAlpha = 1;
      }
    }
    const c = strikeAt(s);
    const h = since(s.t, STROKE_HIT);
    hitStar(g, s, c, h, 'ink', 1.1);
    spatter(g, c, h, 10, sideOf(s) > 0 ? 0.3 : Math.PI - 0.3, 1.6, 2.6, 13);
    needleBurst(g, [c[0], c[1] - 4], h - 1, 3, 17, 11);
  },
};

// ── 2. jz-poem (lock): three stabs, each writing a mark of a five-mark verse column; the column rains on the foe ──

const POEM_HITS = [0.45, 0.8, 1.15] as const;
/** The five marks: short original strokes in a mark box (x, y offsets in cells, curve relative to the mark's centre). */
const MARKS: readonly (readonly number[])[] = [
  [-2, -3, 0, -1, 2, 2], // a dot
  [-5, 0, 0, -2, 5, 0], // a level stroke
  [-1, -4, 1, 0, -2, 4], // a hooked fall
  [3, -3, 0, 0, -3, 3], // a leftward sweep
  [-4, -1, 0, 2, 4, 2], // a lifted stroke
];
const MW = [6, 5, 5, 5, 5] as const;
const markAt = (s: Scene, i: number): V => {
  const sx = sideOf(s);
  return [s.f[0] + sx * 20, s.f[1] - s.chest * 2.0 + i * Math.round(s.chest * 0.38)];
};
const markBorn = (i: number): number => (i < 3 ? POEM_HITS[i]! : POEM_HITS[2] + 0.08 + (i - 3) * 0.08);
const KM: number[] = [0, 0, 0, 0, 0, 0];
const POEM_RAIN = 1.65;
const poem: ArtFx = {
  id: 'brush-poem',
  skill: 'jz-poem',
  th: 'jz-poem',
  on: true,
  note: 'แทงสามจังหวะ แต่ละแทงลากเส้นหมึกตรงเข้าเป้าและเขียนตัวหมึกหนึ่งตัวลงแถวตั้งข้างเป้า (จุด ขีด ตวัด) -> แทงที่สามแรงสุด เติมอีกสองตัวครบห้า แถวกลอนสว่างวาบ -> ตัวหมึกทั้งแถวหยดลงเป็นฝนหมึกบนเป้า เป็นรอยบนพื้น จาง',
  weapon: 'sword',
  acts: [
    { m: 'wxThrust', t0: 0.2, t1: 0.55 },
    { m: 'wxThrust', t0: 0.58, t1: 0.9 },
    { m: 'wxThrust', t0: 0.93, t1: 1.35 },
  ],
  hits: [...POEM_HITS],
  big: POEM_HITS[2],
  ghosts: [0.4, 0.75, 1.1],
  stepIn: 8,
  pre: 0.3,
  hit: POEM_HITS[0],
  end: 2.3,
  afterimage: 'port',
  under(g, s) {
    // The rain's marks on the ground round the foe.
    for (let i = 0; i < 5; i++) {
      const e = since(s.t, POEM_RAIN + 0.12 + i * 0.04);
      if (e < 0 || e >= 10) continue;
      const p = markAt(s, i);
      blot(g, p[0] - sideOf(s) * (6 + i * 3), s.f[1] + 1 + (i % 2) * 3, 2 + Math.min(e, 2), 60 + i, fade(e, 5, 8), 0.5);
    }
  },
  over(g, s) {
    const c = strikeAt(s);
    const sx = sideOf(s);
    // Each stab: a straight ink jab along the thrust into the foe, fresh for two frames, then thinned.
    POEM_HITS.forEach((at, i) => {
      const e = since(s.t, at - 1 / 12);
      if (e < 0 || e >= 5) return;
      const L = i === 2 ? 34 : 26;
      const a = add(c, s.d, -L);
      const b = add(c, s.d, 2);
      KM[0] = a[0];
      KM[1] = a[1] - 1;
      KM[2] = (a[0] + b[0]) / 2;
      KM[3] = (a[1] + b[1]) / 2 - 2;
      KM[4] = b[0];
      KM[5] = b[1];
      brush(g, KM, i === 2 ? 5 : 4, e === 0 ? 0.6 : 1, 0.6, 70 + i, e < 2 ? 3 : e < 4 ? 2 : 1);
      hitStar(g, s, c, since(s.t, at), 'ink', i === 2 ? 1.3 : 0.8);
      spatter(g, c, since(s.t, at), 5, sx > 0 ? -0.2 : Math.PI + 0.2, 1.4, 2.2, 73 + i, 7);
    });
    // The column: each mark written in two frames, held, lit together after the fifth, then falling as ink rain.
    for (let i = 0; i < 5; i++) {
      const born = markBorn(i);
      const e = since(s.t, born);
      if (e < 0) continue;
      const p = markAt(s, i);
      const m = MARKS[i]!;
      const fall = s.t > POEM_RAIN + i * 0.04 ? Math.pow((s.t - POEM_RAIN - i * 0.04) * 12, 2) * 0.45 : 0;
      if (fall > s.f[1] - p[1]) continue;
      for (let q = 0; q < 6; q += 2) {
        KM[q] = p[0] + m[q]! * sx * 1.7;
        KM[q + 1] = p[1] + m[q + 1]! * 1.7 + fall;
      }
      const lit = since(s.t, markBorn(4) + 1 / 12);
      const lv = fall > 0 ? 2 : 3;
      brush(g, KM, MW[i]!, e === 0 ? 0.5 : 1, 0.35, 80 + i, lv);
      if (lit >= 0 && lit < 3 && fall === 0) {
        level(g, 3);
        px(g, KM[0]! + sx, KM[1]! - 1, C('white', 8));
        g.globalAlpha = 1;
      }
    }
    // The column's spine: a fine dry rule drawn down the verse once it is whole.
    const sp = since(s.t, markBorn(4) + 1 / 12);
    if (sp >= 0 && s.t < POEM_RAIN) {
      const top = markAt(s, 0);
      const bot = markAt(s, 4);
      const x = top[0] + sx * 11;
      level(g, sp < 2 ? 3 : 2);
      line(g, [x, top[1] - 6], [x, lerp(top, bot, clamp01((sp + 1) / 2))[1] + 6], C('ink', 5));
      g.globalAlpha = 1;
    }
  },
};

// ── 3. jz-ink: a gob of ink flicked onto the marked area — a ground splash that pools and dries ──

const INK_R = Math.min(64, tilesPx(2.4));
const INK_HIT = 0.8;
const INK_FLY = 0.6;
const inkFx: ArtFx = {
  id: 'brush-ink',
  skill: 'jz-ink',
  th: 'jz-ink',
  on: true,
  note: 'วงเขตหมึกขีดรอบที่หมาย หยดหมึกหมุนวนเข้ารวมเป็นก้อนที่ปลายกระบี่ -> สะบัด ก้อนหมึกลอยโค้งไปตกกลางเขต -> หมึกแตกกระจายเป็นดาวหมึกใหญ่บนพื้น แฉกสาดออก หยดหมึกเด้งขึ้น -> เป็นแอ่งหมึกค้าง (ช้า) ใบสนลอยบนแอ่ง แห้งจางหาย',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.1, t1: INK_FLY },
    { m: 'wxCleave', t0: INK_FLY, t1: 1.05 },
  ],
  hits: [INK_HIT],
  big: INK_HIT,
  cast: [0.1, INK_FLY],
  pre: 0.55,
  hit: INK_HIT,
  end: 3.2,
  afterimage: null,
  under(g, s) {
    const c = s.f;
    // The tell: the area ring brushed on, dithered.
    if (s.t >= 0.25 && s.t < INK_HIT + 0.1) {
      level(g, 3);
      ringOn(g, c, INK_R, -Math.PI / 2, (s.t - 0.25) / 0.3, C('ink', 5), false);
      g.globalAlpha = 1;
    }
    const e = since(s.t, INK_HIT);
    if (e < 0 || e >= 28) return;
    // Pooled (the slow) for a second and a half, drying inward, then dithered away.
    const lv = e < 22 ? 3 : e < 25 ? 2 : 1;
    const grow = e === 0 ? 0.6 : 1 - 0.45 * clamp01((e - 12) / 12);
    // The pool: a ragged ground blot in the middle.
    blot(g, c[0], c[1], INK_R * 0.36 * grow, 91, lv, 0.5);
    // The splash: tapered ground strokes thrown out from the pool, each ending in a blot; satellites between.
    for (let i = 0; i < 11; i++) {
      const a = (i / 11) * Math.PI * 2 + hash(i, 92) * 0.4;
      const L = INK_R * (0.55 + hash(i, 93) * 0.45) * grow;
      const r0 = INK_R * 0.25;
      KM[0] = c[0] + Math.cos(a) * r0;
      KM[1] = c[1] + Math.sin(a) * r0 * 0.5;
      KM[2] = c[0] + Math.cos(a + 0.12) * (r0 + L) * 0.6;
      KM[3] = c[1] + Math.sin(a + 0.12) * (r0 + L) * 0.3;
      KM[4] = c[0] + Math.cos(a) * L;
      KM[5] = c[1] + Math.sin(a) * L * 0.5;
      brush(g, KM, i % 3 === 0 ? 4 : 3, 1, 0.3, 94 + i, lv, e < 14 ? 1 : 2);
      blot(g, KM[4]!, KM[5]!, i % 2 ? 2 : 1.4, 95 + i, Math.min(lv, 3), 0.5);
    }
    for (let i = 0; i < 16; i++) {
      const a = hash(i, 96) * Math.PI * 2;
      const r = INK_R * (0.4 + hash(i, 97) * 0.65) * grow;
      level(g, Math.min(lv, 3));
      inkDrop(g, c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r * 0.5, i % 3 === 0);
    }
    g.globalAlpha = 1;
    // Floating on the pool: a few pine-needle pairs.
    if (e >= 4) {
      level(g, Math.min(lv, 3));
      for (let i = 0; i < 4; i++) needles(g, c[0] + (hash(i, 98) - 0.5) * INK_R * 0.5, c[1] + (hash(i, 99) - 0.5) * INK_R * 0.2, i * 3 + 1);
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    const c = s.f;
    // Gather: ink drops spiralling in to the tip, the gob swelling there.
    if (s.t >= 0.15 && s.t < INK_FLY + 0.05) {
      const { tip } = s.limbs(s.t);
      const k = clamp01((s.t - 0.15) / (INK_FLY - 0.15));
      level(g, 3);
      for (let i = 0; i < 7; i++) {
        const u = (k * 1.6 + i / 7) % 1;
        const a = u * Math.PI * 3 + i;
        const r = 18 * (1 - u) + 1;
        inkDrop(g, tip[0] + Math.cos(a) * r, tip[1] + Math.sin(a) * r * 0.6, i % 3 === 0);
      }
      g.globalAlpha = 1;
      blot(g, tip[0], tip[1], 1.5 + k * 2.5, 100, 3);
    }
    // The flight: the gob in an arc from the tip to the middle of the area, three drops trailing.
    if (s.t >= INK_FLY && s.t < INK_HIT) {
      const from = s.limbs(INK_FLY).tip;
      for (let j = 3; j >= 0; j--) {
        const u = clamp01((s.t - INK_FLY - j * 0.025) / (INK_HIT - INK_FLY));
        const p = lerp(from, c, u);
        const y = p[1] - Math.sin(u * Math.PI) * s.chest * 1.6;
        if (j === 0) blot(g, p[0], y, 4, 101, 3);
        else {
          level(g, j < 2 ? 3 : 2);
          inkDrop(g, p[0], y, j === 3);
          g.globalAlpha = 1;
        }
      }
    }
    // The splash thrown up, falling back on the area.
    const e = since(s.t, INK_HIT);
    spatter(g, [c[0], c[1] - 2], e, 14, -Math.PI / 2, 2.6, 3.2, 102, 10, 0.45);
    if (e >= 0 && e < 2) {
      level(g, 3);
      diamond(g, c[0], c[1] - 3, e === 0 ? 3 : 2, C('ink', 3), C('white', 8));
      g.globalAlpha = 1;
    }
  },
};

// ── 4. jz-seal: a needle-fine jab to a vital point, the cinnabar seal stamped on it, the meridians shut ──

const SEAL_HIT = 0.6;
const seal: ArtFx = {
  id: 'brush-seal',
  skill: 'jz-seal',
  th: 'jz-seal',
  on: true,
  note: 'เส้นบางสี่เส้นบีบเข้าหาปลายกระบี่ หยดหมึกหนึ่งเม็ด (เล็งจุด) -> จิ้มเป็นเส้นเข็มตรงเข้าจุดชีพจร จุดหมึก -> ตราแดงสี่เหลี่ยมกดลงบนจุด (บี้ตอนกด) ลายสลักสนขาว กรอบสี่เหลี่ยมแผ่ออก เส้นชีพจรสี่เส้นหุบเข้าหาตรา (ปิดกระบวนท่า) -> ตราจางหดเหลือรอยเล็ก',
  weapon: 'sword',
  acts: [{ m: 'wxThrust', t0: 0.2, t1: 1.0 }],
  hits: [SEAL_HIT],
  big: SEAL_HIT,
  ghosts: [0.5, 0.56],
  stepIn: 6,
  pre: 0.4,
  hit: SEAL_HIT,
  end: 1.9,
  afterimage: 'port',
  over(g, s) {
    // Aim: four fine lines closing on the tip, one ink bead.
    if (s.t >= 0.22 && s.t < 0.5) {
      const { tip } = s.limbs(s.t);
      const k = clamp01((s.t - 0.22) / 0.22);
      const r = 14 - k * 10;
      level(g, 2);
      for (let i = 0; i < 4; i++) {
        const a = Math.PI / 4 + (i * Math.PI) / 2;
        line(g, [tip[0] + Math.cos(a) * (r + 5), tip[1] + Math.sin(a) * (r + 5)], [tip[0] + Math.cos(a) * r, tip[1] + Math.sin(a) * r], C('white', 7));
      }
      g.globalAlpha = 1;
      blot(g, tip[0], tip[1] + 1, 1.5, 110, 3);
    }
    const c = strikeAt(s, 1.1);
    // The jab: a needle line from the tip to the point, two frames.
    const j = since(s.t, SEAL_HIT - 2 / 12);
    if (j >= 0 && j < 4) {
      const tip = s.limbs(Math.min(s.t, SEAL_HIT)).tip;
      level(g, j < 3 ? 3 : 2);
      line(g, tip, c, C('white', 8));
      line(g, [tip[0], tip[1] + 1], [c[0], c[1] + 1], C('ink', 2));
      g.globalAlpha = 1;
    }
    const h = since(s.t, SEAL_HIT);
    if (h < 0 || h >= 16) return;
    // The dot on the point, under the seal.
    blot(g, c[0], c[1], 3, 111, fade(h, 10, 13), 1, h < 3 ? 4 : 0);
    // The seal: dropped from above (half size, faint), pressed (squashed), held, then faded and shrunk.
    const sy = c[1] - 3;
    if (h === 0) chop(g, c[0], sy - 10, 1, 2);
    else if (h === 1) chop(g, c[0], sy, 2, 3, 0.72);
    else if (h < 10) chop(g, c[0], sy, 2, 3);
    else chop(g, c[0], sy, 1, h < 13 ? 2 : 1);
    // The press: a square ring of ink specks thrown out, and a square frame pulsing out.
    if (h >= 1 && h < 6) {
      const R = 12 + h * 3;
      level(g, fade(h, 3, 5));
      for (let i = -R; i <= R; i += 3) {
        px(g, c[0] + i, sy - R, C('maroon', 5));
        px(g, c[0] + i, sy + R, C('maroon', 5));
        px(g, c[0] - R, sy + i, C('maroon', 5));
        px(g, c[0] + R, sy + i, C('maroon', 5));
      }
      g.globalAlpha = 1;
    }
    // The meridians shut: four ink lines run in from the body's edges to the seal's corners.
    if (h >= 2 && h < 9) {
      const k = clamp01((h - 2) / 3);
      level(g, h < 7 ? 3 : 2);
      for (const [dx, dy] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ] as const) {
        const far: V = [c[0] + dx * 26, sy + dy * 22];
        const near: V = [c[0] + dx * 11, sy + dy * 11];
        const p0 = lerp(far, near, k * 0.6);
        const p1 = lerp(far, near, Math.min(1, k + 0.3));
        line(g, p0, p1, C('maroon', 4));
        line(g, [p0[0], p0[1] + 1], [p1[0], p1[1] + 1], C('maroon', 3));
        if (k >= 1) px(g, near[0], near[1], C('maroon', 6));
      }
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, h, 'maroon', 0.8);
  },
};

// ── 5. jz-ward (buff): a folding fan painted with a pine opens behind the body, ribs glinting; it folds to an ink ring ──

const FAN_R = 38;
const FAN_IN = 13;
const FAN_RIBS = 11;
const FAN_A0 = Math.PI * 1.08;
const FAN_A1 = Math.PI * 1.92;
const fanOpen = (t: number): number => (t < 0.2 ? 0 : t < 1.85 ? clamp01((t - 0.2) / 0.35) : 1 - clamp01((t - 1.85) / 0.25));
const ward: ArtFx = {
  id: 'brush-ward',
  skill: 'jz-ward',
  th: 'jz-ward',
  on: true,
  note: 'พัดจีบกางออกทีละซี่ด้านหลังร่าง กระดาษขาวขอบหมึก วาดกิ่งสนหมึกเขียวบนพัด -> พัดค้าง ปลายซี่ประกายวิ่งทีละซี่ ลมหมึกวนสองสายรอบตัว (ตั้งรับรอ) -> พัดหุบกลับเป็นเส้นเดียวแล้วหาย เหลือวงหมึกบางที่เท้า จาง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.0 }],
  hits: [],
  pre: 0.1,
  hit: 0.6,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    // The ink ring left at the feet once the fan folds (the ward waiting).
    const r = since(s.t, 1.95);
    if (r >= 0 && s.t < 2.55) {
      level(g, r < 4 ? 3 : r < 6 ? 2 : 1);
      isoRing(g, s.a[0], s.a[1], 20, C('ink', 1), true);
      g.globalAlpha = 1;
    }
    const k = fanOpen(s.t);
    if (k <= 0) return;
    const sx = sideOf(s);
    const o: V = [s.a[0] - sx * 3, s.a[1] - s.chest * 0.75];
    const lv = s.t < 1.85 ? 3 : 2;
    // The fan spans from its fixed outer rib round to the moving one (opening from the caster's back side).
    const aFix = sx > 0 ? FAN_A0 : FAN_A1;
    const aMov = aFix + (sx > 0 ? 1 : -1) * (FAN_A1 - FAN_A0) * k;
    const lo = Math.min(aFix, aMov);
    const hi = Math.max(aFix, aMov);
    // The paper: cells between the inner and outer radius, white, a band of shade at each fold.
    level(g, lv);
    for (let y = Math.floor(o[1] - FAN_R - 1); y <= o[1] + 2; y++)
      for (let x = Math.floor(o[0] - FAN_R - 1); x <= o[0] + FAN_R + 1; x++) {
        const dx = x - o[0];
        const dy = y - o[1];
        const d = Math.hypot(dx, dy);
        if (d < FAN_IN || d > FAN_R) continue;
        let a = Math.atan2(dy, dx);
        if (a < 0) a += Math.PI * 2;
        if (a < lo || a > hi) continue;
        const fold = ((a - FAN_A0) / ((FAN_A1 - FAN_A0) / (FAN_RIBS - 1))) % 1;
        px(g, x, y, d > FAN_R - 1.5 ? C('ink', 1) : fold < 0.18 ? C('white', 5) : C('white', 7));
      }
    // The ribs (dark lines from the pivot to the paper's top edge).
    for (let i = 0; i < FAN_RIBS; i++) {
      const a = FAN_A0 + ((FAN_A1 - FAN_A0) * i) / (FAN_RIBS - 1);
      if (a < lo - 1e-3 || a > hi + 1e-3) continue;
      line(g, o, [o[0] + Math.cos(a) * FAN_R, o[1] + Math.sin(a) * FAN_R], C('brown', 2));
      // Held: a glint running rib to rib along the top edge.
      if (k >= 1 && s.t < 1.85 && (since(s.t, 0.6) + i) % FAN_RIBS === 0) {
        level(g, 3);
        diamond(g, o[0] + Math.cos(a) * (FAN_R + 1), o[1] + Math.sin(a) * (FAN_R + 1), 1, C('white', 6), C('white', 8));
        level(g, lv);
      }
    }
    g.globalAlpha = 1;
    // The painting: a pine sprig across the open paper, brushed on once the fan is open.
    if (k >= 1) {
      const pk = clamp01((s.t - 0.55) / 0.3);
      pine(g, o[0] - sx * 20, o[1] - 22, sx > 0 ? -0.3 : Math.PI + 0.3, 24, s.t < 1.85 ? 3 : 2, pk);
      // A dry ink hill under the sprig.
      KM[0] = o[0] - sx * 24;
      KM[1] = o[1] - 16;
      KM[2] = o[0] - sx * 6;
      KM[3] = o[1] - 27;
      KM[4] = o[0] + sx * 18;
      KM[5] = o[1] - 17;
      brush(g, KM, 3, pk, 0.8, 120, s.t < 1.85 ? 3 : 2, 2);
    }
    // The pivot rivet.
    level(g, 3);
    g.fillStyle = C('gold', 4);
    g.fillRect(Math.round(o[0]) - 1, Math.round(o[1]) - 1, 2, 2);
    g.globalAlpha = 1;
  },
  over(g, s) {
    // The ward's wind: two ink streams circling the body, front halves (held while the fan is open).
    if (s.t >= 0.6 && s.t < 1.9) {
      const lv = s.t < 1.7 ? 3 : 2;
      for (let j = 0; j < 2; j++) {
        const y = s.a[1] - s.chest * (0.5 + j * 0.7);
        const ph = (s.t - 0.6) * 7 + j * Math.PI;
        for (let q = 0; q < 10; q++) {
          const a = ph - q * 0.16;
          if (Math.sin(a) < 0) continue;
          level(g, q < 3 ? lv : Math.min(lv, 2));
          const x = s.a[0] + Math.cos(a) * 18;
          const yy = y + Math.sin(a) * 6;
          px(g, x, yy, q === 0 ? C('white', 7) : C('ink', 2));
          if (q < 4) px(g, x, yy + 1, C('ink', 1));
        }
      }
      g.globalAlpha = 1;
    }
    // Folded: a few pine needles shaken off the shut fan.
    needleBurst(g, [s.a[0] - sideOf(s) * 22, s.a[1] - s.chest * 1.4], since(s.t, 2.0), 4, 121, 9, 1.6);
  },
};

// ── 6. jz-focus (buff): an ink circle brushed round the feet in one stroke, one drop, pine tufts held still ──

const ENSO_R = 32;
/** A brush stroke on the ground ellipse round `c` (radius r, iso 2:1) from `a0` turning `sweep`, drawn to `upto`. */
function groundBrush(g: G, c: V, r: number, a0: number, sweep: number, upto: number, w: number, dry: number, seed: number, lv: number): void {
  if (upto <= 0 || lv <= 0) return;
  const n = Math.ceil(Math.abs(sweep) * r * 1.8);
  level(g, lv);
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    if (u > upto) break;
    const a = a0 + sweep * u;
    const prof = u < 0.08 ? 1.25 - u * 2 : u < 0.75 ? 1.05 : Math.max(0.4, (1 - u) / 0.25);
    const rows = Math.max(1, Math.round(w * prof));
    const thr = dry * clamp01((u - 0.6) / 0.4);
    if (dry > 0 && u > 0.93 && (i >> 1) % 2 === 1) continue;
    for (let q = 0; q < rows; q++) {
      const rr = r + q - (rows - 1) / 2;
      const streak = q > 0 && q < rows - 1 && hash(q * 11 + 3, seed) < thr;
      px(g, c[0] + Math.cos(a) * rr, c[1] + Math.sin(a) * rr * 0.5, streak ? C('white', 3) : q === rows - 1 && rows > 1 ? C('ink', 5) : C('ink', 1));
    }
  }
  g.globalAlpha = 1;
}
const TUFTS = 3;
const tuftAt = (s: Scene, i: number): V => {
  const a = (i / TUFTS) * Math.PI * 2 - Math.PI / 2 + 0.4;
  const rise = clamp01((s.t - 0.85 - i * 0.06) / 0.3);
  const fall = s.t > 2.0 ? Math.pow((s.t - 2.0) * 12, 2) * 0.35 : 0;
  return [s.a[0] + Math.cos(a) * 20, s.a[1] + Math.sin(a) * 8 - rise * s.chest * 1.3 + fall];
};
/** A held pine tuft: three needle pairs fanned upward round a brown knot. */
function tuft(g: G, p: V, lv: number): void {
  level(g, lv);
  needles(g, p[0] - 2, p[1], 5);
  needles(g, p[0], p[1] - 1, 6);
  needles(g, p[0] + 2, p[1], 7);
  g.globalAlpha = 1;
}
const focus: ArtFx = {
  id: 'brush-focus',
  skill: 'jz-focus',
  th: 'jz-focus',
  on: true,
  note: 'ปลายกระบี่วาดวงหมึกรอบเท้าในพู่กันเดียว หัวหนา ปลายแห้งเว้นช่อง -> หยดหมึกหยดเดียวจากปลายกระบี่ตกกลางวง คลื่นหมึกแผ่หนึ่งวง -> พู่ใบสนสามพู่ลอยขึ้นมาค้างนิ่งรอบตัว ประกายวิ่งขึ้นใบกระบี่ (จิตนิ่ง) -> ใบสนร่วง วงหมึกจาง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.0 }],
  hits: [],
  pre: 0.1,
  hit: 0.75,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    if (s.t >= 0.2 && s.t < 2.5) {
      const lv = s.t < 1.95 ? 3 : s.t < 2.2 ? 2 : 1;
      groundBrush(g, s.a, ENSO_R, Math.PI * 0.62, -Math.PI * 1.82, clamp01((s.t - 0.2) / 0.35), 7, 0.6, 130, lv);
    }
    // The drop lands: one ink ring spreading inside the circle.
    const e = since(s.t, 0.75);
    if (e >= 0 && e < 7) {
      level(g, fade(e, 3, 5));
      isoRing(g, s.a[0], s.a[1], 4 + e * 3, e < 2 ? C('ink', 1) : C('ink', 5), e < 3);
      g.globalAlpha = 1;
    }
    for (let i = 0; i < TUFTS; i++) if (Math.sin((i / TUFTS) * Math.PI * 2 - Math.PI / 2 + 0.4) < 0 && s.t >= 0.85 && s.t < 2.3) {
      const p = tuftAt(s, i);
      tuft(g, p, s.t < 1.95 ? 3 : 2);
    }
  },
  over(g, s) {
    // One drop from the tip, falling to the middle of the circle.
    if (s.t >= 0.5 && s.t < 0.75) {
      const tip = s.limbs(0.5).tip;
      const k = clamp01((s.t - 0.58) / 0.17);
      const y = tip[1] + k * k * (s.a[1] - tip[1]);
      blot(g, lerp(tip, s.a, k)[0], y, s.t < 0.58 ? 1.2 : 2, 131, 3);
    }
    spatter(g, [s.a[0], s.a[1] - 1], since(s.t, 0.75), 6, -Math.PI / 2, 2.2, 1.6, 132, 6, 0.4);
    // The tufts held motionless in front (the back ones are drawn under the body).
    for (let i = 0; i < TUFTS; i++) if (Math.sin((i / TUFTS) * Math.PI * 2 - Math.PI / 2 + 0.4) >= 0 && s.t >= 0.85 && s.t < 2.3) {
      const p = tuftAt(s, i);
      tuft(g, p, s.t < 1.95 ? 3 : 2);
    }
    // Stillness: a glint running up the blade, then the tip lit and held.
    const e = since(s.t, 1.0);
    if (e >= 0 && s.t < 1.9) {
      const { hand, tip } = s.limbs(s.t);
      const p = lerp(hand, tip, clamp01((e + 1) / 4));
      level(g, 3);
      diamond(g, p[0], p[1], e < 4 ? 2 : 1, C('ink', 3), C('white', 8));
      g.globalAlpha = 1;
    }
  },
};

// ── 7. jz-break (break): an original glyph written in the air stroke by stroke, driven into the foe and shattered ──

/** The glyph: five strokes in a unit box (x right toward the foe, y down), each [x0, y0, cx, cy, x1, y1, width]. */
const GLYPH: readonly (readonly number[])[] = [
  [-0.12, -1.0, 0.0, -0.9, 0.1, -0.72, 5],
  [-0.85, -0.5, 0.0, -0.66, 0.88, -0.42, 5],
  [-0.3, -0.62, -0.05, 0.2, -0.22, 0.88, 5],
  [0.2, -0.2, 0.55, 0.3, 0.3, 0.85, 5],
  [-0.6, 0.1, -0.7, 0.4, -0.95, 0.5, 4],
];
const BRK_HIT = 0.75;
const BRK_WRITE = [0.12, 0.5] as const;
const glyphAt = (s: Scene, t: number): { c: V; k: number } => {
  const home: V = [lerp(s.a, s.f, 0.45)[0], s.a[1] - s.chest * 1.45];
  const u = clamp01((t - (BRK_HIT - 2 / 12)) / (2 / 12));
  return { c: lerp(home, strikeAt(s), u), k: 23 + u * 5 };
};
const KG: number[] = [0, 0, 0, 0, 0, 0];
const breakFx: ArtFx = {
  id: 'brush-break',
  skill: 'jz-break',
  th: 'jz-break',
  on: true,
  note: 'วงอำพันที่เท้า (สัญญาณท่าทำลาย) ปลายกระบี่เขียนตัวอักษรใหญ่กลางอากาศทีละขีด (ห้าขีด ตัวคิดขึ้นเอง) -> ตัวอักษรสว่างวาบ ฟันลง ตัวอักษรพุ่งใหญ่ขึ้นใส่เป้า เงาร่างแยก -> กระแทก ตัวอักษรแตกเป็นขีดหมึกกระเด็นรอบทิศ หมึกสาด -> เศษหมึกร่วง จาง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 0.55 },
    { m: 'wxCleave', t0: 0.55, t1: 1.05 },
  ],
  hits: [BRK_HIT],
  big: BRK_HIT,
  ghosts: [0.62, 0.68],
  stepIn: 6,
  cast: [0.1, 0.55],
  pre: 0.55,
  hit: BRK_HIT,
  end: 1.95,
  afterimage: 'port',
  under(g, s) {
    // The guard-break tell: an amber ring drawn on round the feet, an ink ring inside.
    if (s.t >= 0.1 && s.t < BRK_HIT) {
      level(g, 3);
      ringOn(g, s.a, 14, -Math.PI / 2, clamp01((s.t - 0.1) / 0.35), C('fire', 4), true);
      level(g, 2);
      ringOn(g, s.a, 10, Math.PI / 2, clamp01((s.t - 0.25) / 0.3), C('ink', 2));
      g.globalAlpha = 1;
    }
    // The shards' landing blots round the foe.
    for (let i = 0; i < 5; i++) {
      const e = since(s.t, BRK_HIT + 0.35 + i * 0.05);
      if (e < 0 || e >= 9) continue;
      const a = (i / 5) * Math.PI * 2 + 0.3;
      blot(g, s.f[0] + Math.cos(a) * 22, s.f[1] + Math.sin(a) * 9, 2 + Math.min(2, e), 140 + i, fade(e, 5, 7), 0.5);
    }
  },
  over(g, s) {
    const sx = sideOf(s);
    const h = since(s.t, BRK_HIT);
    if (h < 0) {
      // Writing: each stroke drawn in turn by the tip; then the whole glyph lit, then flung at the foe.
      const { c, k } = glyphAt(s, s.t);
      const lit = s.t >= BRK_WRITE[1] + 0.02 && s.t < BRK_WRITE[1] + 0.12;
      GLYPH.forEach((st, i) => {
        const t0 = BRK_WRITE[0] + ((BRK_WRITE[1] - BRK_WRITE[0]) * i) / GLYPH.length;
        const t1 = t0 + (BRK_WRITE[1] - BRK_WRITE[0]) / GLYPH.length;
        if (s.t < t0) return;
        for (let q = 0; q < 6; q += 2) {
          KG[q] = c[0] + st[q]! * k * sx;
          KG[q + 1] = c[1] + st[q + 1]! * k;
        }
        brush(g, KG, st[6]!, clamp01((s.t - t0) / (t1 - t0)), 0.5, 150 + i, 3);
      });
      if (lit) {
        level(g, 3);
        diamond(g, c[0] + sx * k * 0.9, c[1] - k, 2, C('white', 6), C('white', 8));
        g.globalAlpha = 1;
      }
    }
    bladeArc(g, s, 0.6, 0.92, 'ink', 4);
    const c = strikeAt(s);
    hitStar(g, s, c, h, 'fire', 1.3);
    // Shattered: each stroke flung out from the foe as a short ink stroke, turning, dithered away.
    if (h >= 0 && h < 10) {
      const lv = fade(h, 4, 7);
      GLYPH.forEach((_st, i) => {
        const a = (i / GLYPH.length) * Math.PI * 2 - Math.PI / 2 + 0.3;
        const r = 8 + h * 4.5;
        const ox = c[0] + Math.cos(a) * r;
        const oy = c[1] + Math.sin(a) * r * 0.7 + h * h * 0.18;
        const turn = h * 0.35 * (i % 2 ? 1 : -1);
        const L = 9 - h * 0.4;
        KG[0] = ox - Math.cos(a + turn) * L;
        KG[1] = oy - Math.sin(a + turn) * L;
        KG[2] = ox;
        KG[3] = oy - 2;
        KG[4] = ox + Math.cos(a + turn) * L;
        KG[5] = oy + Math.sin(a + turn) * L;
        brush(g, KG, 4, 1, 0.5, 160 + i, lv);
      });
    }
    spatter(g, c, h, 12, -Math.PI / 2, Math.PI * 1.6, 3, 170, 10, 0.35);
    needleBurst(g, c, h - 1, 4, 171, 12, 2.6);
  },
};

// ── 8. jz-scroll (ท่าพลัง): a scroll unrolled over the head, written, wrapped round the caster, burst outward ──
// Beats: 0.1 the rolled scroll rises over the head; 0.45-0.85 it unrolls both ways (rollers, paper, ink columns and a
// pine written on); 1.05 it wraps down round the caster as a paper ring on the ground; 1.2 the ring bursts (the blow):
// the written marks fly out as strokes, an ink ring splashes at the edge, pine and paper shreds; then they settle.

const SC_R = Math.min(76, tilesPx(3));
const SC_HIT = 1.2;
const SC_WRAP = 1.05;
const SC_W = 40;
const scrollOpen = (t: number): number => clamp01((t - 0.45) / 0.4);
const scrollY = (s: Scene): number => s.a[1] - s.chest * 2 - 12;
const scroll: ArtFx = {
  id: 'brush-scroll',
  skill: 'jz-scroll',
  th: 'jz-scroll',
  on: true,
  note: 'ท่าพลัง: ม้วนคัมภีร์ลอยขึ้นเหนือหัว คลี่ออกสองข้าง แกนไม้ปลายทอง ตัวหมึกเขียนลงทีละแถว กิ่งสนวาดมุม วงเขตหมึกบนพื้น -> คัมภีร์ม้วนลงพันรอบตัวเป็นวงกระดาษบนพื้น -> ระเบิดออก ตัวหมึกพุ่งออกเป็นขีดรอบทิศ วงหมึกสาดที่ขอบเขต ใบสนกับเศษกระดาษปลิว -> เศษกระดาษร่วงช้า หมึกแห้งจาง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 1.05 },
    { m: 'wxCleave', t0: 1.05, t1: 1.45 },
  ],
  hits: [SC_HIT],
  big: SC_HIT,
  cast: [0.1, 1.0],
  pre: 0.9,
  hit: SC_HIT,
  end: 2.7,
  afterimage: null,
  beats: [
    { t: 0.1, th: 'ม้วนคัมภีร์ลอย' },
    { t: 0.45, th: 'คลี่' },
    { t: SC_WRAP, th: 'พันรอบตัว' },
    { t: SC_HIT, th: 'ระเบิด' },
    { t: 1.8, th: 'กระดาษร่วง' },
  ],
  under(g, s) {
    const c = s.a;
    // The area tell.
    if (s.t >= 0.4 && s.t < SC_HIT + 0.1) {
      level(g, 2);
      ringOn(g, c, SC_R, -Math.PI / 2, (s.t - 0.4) / 0.4, C('ink', 2), true);
      g.globalAlpha = 1;
    }
    // Wrapped: the paper ring round the feet, written marks on it (back half here, front half over the body).
    const w = since(s.t, SC_WRAP);
    if (w >= 0 && s.t < SC_HIT) paperRing(g, c, 22, false, s.t);
    // The burst: the ring thrown out to the edge, an ink splash ring there, the ground darkened then dried.
    const e = since(s.t, SC_HIT);
    if (e >= 0 && e < 20) {
      const lv = e < 9 ? 3 : e < 14 ? 2 : 1;
      if (e < 4) {
        level(g, 3);
        isoRing(g, c[0], c[1], 22 + (SC_R - 22) * ((e + 1) / 4), e < 2 ? C('white', 7) : C('ink', 2), true);
        g.globalAlpha = 1;
      }
      if (e >= 2) {
        for (let i = 0; i < 14; i++) {
          const a = (i / 14) * Math.PI * 2 + hash(i, 180) * 0.3;
          const rr = SC_R * (0.9 + hash(i, 181) * 0.15);
          blot(g, c[0] + Math.cos(a) * rr, c[1] + Math.sin(a) * rr * 0.5, 2.5 + hash(i, 182) * 2, 183 + i, lv, 0.5, i % 3 === 0 ? 2 : 0);
        }
        groundBrush(g, c, SC_R * 0.82, 0.4, Math.PI * 1.7, 1, 3, 0.85, 184, Math.min(lv, 2));
      }
    }
    // The shreds' landings: white paper bits resting on the ground, dithered.
    const f = since(s.t, 1.9);
    if (f >= 0 && f < 10) {
      level(g, fade(f, 5, 8));
      for (let i = 0; i < 10; i++) {
        const a = hash(i, 185) * Math.PI * 2;
        const r = SC_R * (0.3 + hash(i, 186) * 0.6);
        g.fillStyle = C('white', 6);
        g.fillRect(Math.round(c[0] + Math.cos(a) * r), Math.round(c[1] + Math.sin(a) * r * 0.5), 2, 1);
      }
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    const c = s.a;
    const y = scrollY(s);
    // The scroll over the head: rolled (rising), unrolled with marks written on, then pulled down to the feet.
    if (s.t >= 0.1 && s.t < SC_WRAP) {
      const rise = clamp01((s.t - 0.1) / 0.25);
      const yy = y + (1 - rise) * 12;
      const k = scrollOpen(s.t);
      const half = 2 + k * SC_W;
      const drop = s.t > 0.95 ? Math.pow((s.t - 0.95) * 12, 2) * 2.2 : 0;
      const lv = rise < 0.5 ? 2 : 3;
      level(g, lv);
      // Paper.
      for (let x = -half; x <= half; x++)
        for (let j = -6; j <= 6; j++)
          px(g, c[0] + x, yy + j + drop, j === -6 || j === 6 ? C('white', 4) : j === -5 ? C('white', 8) : C('white', 7));
      // Written: columns of marks (short original strokes) appearing right to left as it opens, a pine at one end.
      const cols = Math.floor((half - 6) / 6);
      for (let q = 0; q < cols; q++) {
        for (const sgn of [-1, 1]) {
          const x = c[0] + sgn * (6 + q * 6);
          for (let r = 0; r < 3; r++) {
            const sh = hash(q * 7 + r + (sgn > 0 ? 50 : 0), 187);
            const yy2 = yy + drop - 4 + r * 4;
            if (sh < 0.3) px(g, x, yy2, C('ink', 1));
            else if (sh < 0.65) line(g, [x - 1, yy2], [x + 1, yy2 + (sh < 0.5 ? 0 : 1)], C('ink', 1));
            else line(g, [x, yy2 - 1], [x - (sh < 0.85 ? 1 : 0), yy2 + 1], C('ink', 1));
          }
        }
      }
      if (k >= 0.9) pine(g, c[0] + half - 12, yy + drop + 4, -Math.PI * 0.7, 7, lv);
      // Rollers: dark wood cylinders at both ends, gold knobs.
      level(g, 3);
      for (const sgn of [-1, 1]) {
        const x = Math.round(c[0] + sgn * (half + 1));
        g.fillStyle = C('brown', 3);
        g.fillRect(x - 1, Math.round(yy + drop) - 8, 3, 17);
        px(g, x + 1, yy + drop - 7, C('brown', 5));
        g.fillStyle = C('gold', 4);
        g.fillRect(x - 1, Math.round(yy + drop) - 10, 3, 2);
        g.fillRect(x - 1, Math.round(yy + drop) + 9, 3, 2);
      }
      g.globalAlpha = 1;
      // Flash as it opens fully.
      if (since(s.t, 0.85) >= 0 && since(s.t, 0.85) < 2) {
        level(g, 3);
        diamond(g, c[0], yy - 10, 2, C('white', 6), C('white', 8));
        g.globalAlpha = 1;
      }
    }
    const w = since(s.t, SC_WRAP);
    if (w >= 0 && s.t < SC_HIT) paperRing(g, c, 22, true, s.t);
    // The burst: the written marks flying out as short ink strokes, radially, over the area.
    const e = since(s.t, SC_HIT);
    if (e >= 0 && e < 10) {
      const lv = fade(e, 5, 8);
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2 + hash(i, 188) * 0.2;
        const r = 26 + e * (SC_R - 22) * 0.13 * (0.8 + hash(i, 189) * 0.4);
        const h0 = s.chest * (0.15 + hash(i, 190) * 0.6) - e * 1.5;
        const ox = c[0] + Math.cos(a) * r;
        const oy = c[1] + Math.sin(a) * r * 0.5 - Math.max(0, h0);
        KG[0] = ox - Math.cos(a) * 7;
        KG[1] = oy - Math.sin(a) * 3.5;
        KG[2] = ox - Math.cos(a) * 2;
        KG[3] = oy - Math.sin(a) * 1 - 2;
        KG[4] = ox + Math.cos(a) * 3;
        KG[5] = oy + Math.sin(a) * 1.5;
        brush(g, KG, 4, 1, 0.55, 191 + i, lv);
      }
    }
    if (e >= 0 && e < 2) {
      level(g, 3);
      diamond(g, c[0], c[1] - s.chest, e === 0 ? 4 : 3, C('ink', 3), C('white', 8));
      g.globalAlpha = 1;
    }
    hitStar(g, s, strikeAt(s), e, 'ink', 1.4);
    spatter(g, [c[0], c[1] - s.chest * 0.4], e - 1, 18, -Math.PI / 2, Math.PI * 2, 3.8, 192, 11, 0.3);
    needleBurst(g, [c[0], c[1] - s.chest * 1.2], e, 8, 193, 16, 3.2);
    // Paper shreds fluttering down over the area.
    const f = since(s.t, SC_HIT + 0.15);
    if (f >= 0 && s.t < 2.5) {
      for (let i = 0; i < 14; i++) {
        const a = hash(i, 194) * Math.PI * 2;
        const r = SC_R * (0.3 + hash(i, 195) * 0.6);
        const gx = c[0] + Math.cos(a) * r + Math.sin(f * 0.6 + i) * 3;
        const top = c[1] - s.chest * (1.8 + hash(i, 196));
        const y = Math.min(c[1] + Math.sin(a) * r * 0.5, top + f * 1.6);
        level(g, s.t < 2.2 ? 3 : 2);
        const flip = (f + i) % 4 < 2;
        g.fillStyle = flip ? C('white', 8) : C('white', 5);
        g.fillRect(Math.round(gx), Math.round(y), flip ? 3 : 2, flip ? 2 : 3);
        if (i % 3 === 0) px(g, gx + 1, y + 1, C('ink', 1));
      }
      g.globalAlpha = 1;
    }
  },
};

/** The scroll wrapped round the caster: a paper band on the ground ellipse (back or front half), marks on it. */
function paperRing(g: G, c: V, r: number, front: boolean, t: number): void {
  const a0 = front ? 0 : Math.PI;
  const n = Math.round(r * 3.2);
  const hgt = 9;
  const k = clamp01((t - SC_WRAP) / 0.08);
  level(g, 3);
  for (let i = 0; i <= n; i++) {
    const a = a0 + (Math.PI * i) / n;
    const x = c[0] + Math.cos(a) * r;
    const y = c[1] + Math.sin(a) * r * 0.5;
    const H = Math.round(hgt * k);
    for (let j = 0; j <= H; j++) px(g, x, y - j, j === H ? C('white', 8) : j === 0 ? C('white', 4) : front ? C('white', 7) : C('white', 5));
    if (front && i % 4 === 1 && H > 5) {
      px(g, x, y - 3, C('ink', 1));
      px(g, x, y - 5 - (i % 3 === 0 ? 1 : 0), C('ink', 1));
    }
  }
  g.globalAlpha = 1;
}

export const BRUSH_FX: readonly ArtFx[] = [stroke, poem, inkFx, seal, ward, focus, breakFx, scroll];
